import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useIntl } from "../../../../shared/core/i18n/use-intl";
import {
  accountManager,
  authExecutor,
  transactionSearchManager,
  trustManager,
} from "../../../../shared/core/service/services";
import { useAccounts } from "../../../../shared/domains/account/use-accounts";
import type { AccountingTransaction } from "../../../../shared/domains/accounting-transaction/transaction/transaction";
import { useTransactionFilter } from "../../../../shared/domains/accounting-transaction/transaction/use-transaction-filter";
import { useTransactions } from "../../../../shared/domains/accounting-transaction/transaction/use-transactions";
import { OnboardingStatus } from "../../../../shared/domains/onboarding/onboarding";
import { useOnboardingFile } from "../../../../shared/domains/onboarding/use-onboarding";
import { useObservable } from "../../../../shared/utils/observable";
import { useAsyncEffect } from "../../../../shared/utils/utils";
import { ACCOUNT_TRANSACTION_DETAILS_MODAL_ID, TRUST_CHOICE_MODAL_ID } from "../../../core/modal/modal-id";
import { Path } from "../../../core/routing/path";
import { Modal } from "../../common/modal/modal";
import { PageAlertsSlot } from "../../common/page/page-alerts-slot";
import { MainColorSpinner } from "../../common/spinner";
import { theme } from "../../styles/theme";
import { UIConstants } from "../../styles/uiConstants";
import { AccountBalanceCard } from "./components/account-balance-card";
import { AccountTransactionDetailsModal } from "./components/accounting-transaction-details-modal";
import { LatestTransactionsSection } from "./components/latest-transactions-section";
import { MonthlyFluxSection } from "./components/monthly-flux-section";
import { RequiredActionsCard } from "./components/required-actions-card";
import { TrustChoiceModal } from "./trust-modal/trust-modal";
import { useSelectedAccount } from "./utils/use-selected-account";

const LATEST_TRANSACTIONS_COUNT = 5;

export default function AccountTransactionsScreen() {
  const accounts = useAccounts();
  const transactionDirection = useObservable(transactionSearchManager.directionSelection);
  const isConnecting = useObservable(authExecutor.isConnecting);
  const loading = useObservable<boolean>(accountManager.loading);
  const refreshing = useObservable(accountManager.refreshing);
  const accountsLoading = loading || refreshing;
  const [isGlobalLoading, setIsGlobalLoading] = useState<boolean>(true);
  const [onboardingIsPendingVerification, setOnboardingIsPendingVerification] = useState<boolean>(false);
  const [focusedAccount] = useSelectedAccount(accounts);
  const { filters } = useTransactionFilter(focusedAccount);
  const transactions = useTransactions(focusedAccount, transactionDirection, filters);
  const { onboardingFile, needOnboardingFileRedirect, loading: onboardingFileLoading } = useOnboardingFile();
  const history = useHistory();
  const { formatMessage } = useIntl();

  const showTrustModal = () => {
    Modal.present(TRUST_CHOICE_MODAL_ID, () => <TrustChoiceModal />, {
      backdropColor: "#f4f4f4",
      backdropOpacity: 1,
      canBeDismissed: false,
    });
  };

  useAsyncEffect(async () => {
    if (isConnecting) {
      const shouldShowTrustModal = !(await trustManager.isValid());
      if (shouldShowTrustModal) {
        showTrustModal();
      } else {
        await trustManager.checkTrusted();
      }
    }
  }, [isConnecting]);

  useEffect(() => {
    if (!onboardingFileLoading) {
      if (needOnboardingFileRedirect) {
        history.push(Path.OnboardingFile);
      }
      setIsGlobalLoading(false);
    }
  }, [onboardingFileLoading]);

  useEffect(() => {
    setOnboardingIsPendingVerification(onboardingFile?.status === OnboardingStatus.PENDING_VERIFICATION);
  }, [onboardingFile]);

  const showTransactionDetails = (transaction: AccountingTransaction) => {
    focusedAccount &&
      Modal.present(
        ACCOUNT_TRANSACTION_DETAILS_MODAL_ID,
        () => <AccountTransactionDetailsModal transaction={transaction} account={focusedAccount} />,
        { canBeDismissed: false },
      );
  };

  if (onboardingIsPendingVerification) {
    return (
      <StyledGlobalContainer>
        <StyledMessageCard>
          <StyledTitle>{formatMessage("accountPendingVerification.title")}</StyledTitle>
          <StyledMessage>{formatMessage("accountPendingVerification.subtitle")}</StyledMessage>
          <UnderlinedButton onClick={() => window.location.reload()}>
            {formatMessage("accountPendingVerification.retry")}
          </UnderlinedButton>
        </StyledMessageCard>
      </StyledGlobalContainer>
    );
  }

  return (
    <>
      {!accountsLoading && accounts.length === 0 ? (
        <NoAccountAvailable>{formatMessage("accountsList.noAccountAvailable")}</NoAccountAvailable>
      ) : (
        !!focusedAccount && (
          <Columns>
            <MainColumn>
              <AccountBalanceCard account={focusedAccount} />
              <MonthlyFluxSection account={focusedAccount} />
              <LatestTransactionsSection
                transactions={transactions.data.slice(0, LATEST_TRANSACTIONS_COUNT)}
                loading={transactions.loading}
                onTransactionClick={showTransactionDetails}
              />
            </MainColumn>
            <SideColumn>
              <AlertsSlot />
              <RequiredActionsCard
                account={focusedAccount}
                toJustifyCount={transactions.unjustifiedTransactions2.total}
                toQualifyCount={transactions.unqualifiedTransactions.total}
              />
            </SideColumn>
          </Columns>
        )
      )}
      {isGlobalLoading && (
        <StyledGlobalLoader>
          <MainColorSpinner size="60" />
        </StyledGlobalLoader>
      )}
    </>
  );
}

const Columns = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  width: 100%;

  @media (max-width: ${UIConstants.DESKTOP_SMALL_BREAKPOINT - 1}px) {
    flex-direction: column;
  }
`;

const MainColumn = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
`;

const SideColumn = styled.div`
  display: flex;
  flex: 0 0 340px;
  flex-direction: column;
  gap: 16px;

  @media (max-width: ${UIConstants.DESKTOP_SMALL_BREAKPOINT - 1}px) {
    flex: 1 1 auto;
    align-self: stretch;
  }
`;

/* The page alerts land here instead of above the content; their stacking is the column's. */
const AlertsSlot = styled(PageAlertsSlot)`
  display: flex;
  flex-direction: column;
  align-self: stretch;
`;

const NoAccountAvailable = styled.span`
  ${theme.text};
  margin-top: 20px;
  margin-bottom: 40px;
`;

const StyledGlobalLoader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.gray[100]};
  z-index: 1000000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledGlobalContainer = styled.div`
  height: 100%;
  margin-top: -20px;
  background-color: ${theme.colors.gray[100]};
`;

const StyledMessageCard = styled.div`
  background-color: #ffecd1;
  border-radius: 13px;
  padding: 16px;
`;

const StyledTitle = styled.div`
  ${theme.bodyBlackBold}
  color: #000000;
  flex-grow: 1;
  flex-shrink: 1;
  padding-right: 8px;
`;

const StyledMessage = styled.div`
  ${theme.text};
  font-size: 14px;
  margin-top: 10px;
  color: ${theme.colors.gray[700]};
`;

const UnderlinedButton = styled.button`
  ${theme.bodyGreyRegular}
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  appearance: none;
  color: #000000;
  font-size: 13px;
  text-decoration: underline;
  margin-top: 16px;
  &:hover {
    text-decoration: none;
  }
`;
